// src/components/PostNewsModal.js
import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { locationData, subCategories, postTypes, SPECIAL_WISHES_POST_TYPE } from '../utils/constants';
import './postNewsModal.css';
import API_BASE_URL from '../utils/apiConfig';
import Select from 'react-select';
import { collection, addDoc, serverTimestamp, doc, updateDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { app, db } from '../firebase-config';
import Chatbot from './Chatbot'; 
const storage = getStorage(app);

const WORD_LIMIT = 50;

/**
 * HELPER: Matches database values (strings/arrays) to React-Select option objects.
 * Handles both single values and arrays of values.
 */
const findOptionsMulti = (options, valuesToMatch) => {
    if (!valuesToMatch) return [];
    
    // Convert single value to array for uniform processing
    const searchArray = Array.isArray(valuesToMatch) 
        ? valuesToMatch.map(v => String(v)) 
        : [String(valuesToMatch)];

    return options.filter(option => {
        const optionVal = typeof option.value === 'object' 
            ? (option.value.type || option.value.state_en || option.value.district_en) 
            : option.value;
            
        return searchArray.includes(String(optionVal));
    });
};
    const cleanTextSpacing = (text) => {
    if (!text) return '';
    
    const lines = text.split('\n');
    // Regex checks if any line starts with numbered list patterns (e.g., "1.", "1)") 
    // or bullet symbols (e.g., "•", "-", "*")
    const hasListItems = lines.some(line => /^\s*(\d+[\.\)]|[-•*])\s*/.test(line));

    if (hasListItems) {
        // If it's a list, preserve line-by-line structure but remove extra blank lines
        return lines
            .map(line => line.replace(/[ \t]+/g, ' ').trim()) // Collapse multiple horizontal spaces
            .filter(line => line.length > 0)                  // Strip out completely empty lines
            .join('\n');                                      // Join back with a single newline
    } else {
        // If it's standard text, collapse everything into a single clean paragraph
        return text.replace(/\s+/g, ' ').trim();
    }
};

    const PostNewsModal = ({ onClose, initialPostData = null, isEditing = false, aiDraft }) => {
    const { currentUser, firebaseUser } = useAuth();
    const [forceHideProfile, setForceHideProfile] = useState(false);
    const [title, setTitle] = useState(aiDraft?.title || '');
    const [content, setContent] = useState(aiDraft?.description || '');
    
    // IMPORTANT: Initialize as empty arrays to prevent .some() errors
    const [postType, setPostType] = useState([]);
    const [state, setState] = useState([]); 
    const [district, setDistrict] = useState([]);
    const [constituency, setConstituency] = useState([]);
    const [mandal, setMandal] = useState([]);
    const [subCategory, setSubCategory] = useState([]);

    // --- NEW MEDIA STATES ---
    const [mediaType, setMediaType] = useState('image'); // 'image' or 'video'
    const [videoFile, setVideoFile] = useState(null);
    const [videoPreview, setVideoPreview] = useState(null);
    const [videoDuration, setVideoDuration] = useState(0);
    const [existingVideoUrl, setExistingVideoUrl] = useState(null);
    
    const [images, setImages] = useState([]);
    const [existingImageUrls, setExistingImageUrls] = useState([]);
    const [imagesToDelete, setImagesToDelete] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [posting, setPosting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Memoized Options
    const postTypeOptions = useMemo(() => {
        let options = [
            { value: { type: 'World' }, label: 'ప్రపంచం' },
            { value: { type: 'India' }, label: 'భారతదేశం' },
            { value: { type: SPECIAL_WISHES_POST_TYPE }, label: 'ప్రత్యేక శుభాకాంక్షలు' }
        ];
        locationData.forEach(s => {
            options.push({ value: { type: s.state_en, isState: true }, label: s.state_te });
        });
        return options;
    }, []);

    const stateOptions = useMemo(() => locationData.map(s => ({
        value: { state_en: s.state_en },
        label: s.state_te,
        en: s.state_en
    })), []);

    const subCategoryOptions = useMemo(() => subCategories, []);

    // --- LOGIC HELPERS ---

    // FIX: Check if AP or TS is in the postType array
    const isStatePostTypeSelected = useMemo(() => {
        if (!Array.isArray(postType)) return false;
        return postType.some(pt => pt.value?.type === 'Andhra Pradesh' || pt.value?.type === 'Telangana');
    }, [postType]);

    // FIX: Check if Wishes is in the postType array
    const shouldShowLocationDropdowns = useMemo(() => {
        if (!Array.isArray(postType)) return false;
        return postType.some(pt => pt.value?.type === SPECIAL_WISHES_POST_TYPE);
    }, [postType]);

    // Helper: Determine which states to use for district filtering
    const statesForDistrictFilter = useMemo(() => {
        if (shouldShowLocationDropdowns) {
            return state.map(s => s.value.state_en);
        } else if (isStatePostTypeSelected) {
            return postType
                .filter(opt => opt.value.type === 'Andhra Pradesh' || opt.value.type === 'Telangana')
                .map(opt => opt.value.type);
        }
        return [];
    }, [postType, state, shouldShowLocationDropdowns, isStatePostTypeSelected]);

    const districtOptions = useMemo(() => {
        if (statesForDistrictFilter.length === 0) return [];
        let options = [];
        locationData.forEach(s => {
            if (statesForDistrictFilter.includes(s.state_en)) {
                s.districts?.forEach(d => {
                    options.push({ value: { district_en: String(d.district_en) }, label: String(d.district_te), en: String(d.district_en) });
                });
            }
        });
        return options; 
    }, [statesForDistrictFilter]);

    const constituencyOptions = useMemo(() => {
        const selectedDistricts = district.map(d => d.value.district_en);
        if (selectedDistricts.length === 0) return [];
        let options = [];
        locationData.forEach(s => {
            s.districts?.forEach(d => {
                if (selectedDistricts.includes(d.district_en)) {
                d.constituencies?.forEach(c => {
                    options.push({
                        ...c,
                        en: String(c.value) // Explicitly set 'en' for searching
                    });
                });
            }
        });
        });
        return options;
    }, [district]);

    const mandalOptions = useMemo(() => {
        const selectedDistricts = district.map(d => d.value.district_en);
        if (selectedDistricts.length === 0) return [];
        let options = [];
        locationData.forEach(s => {
            s.districts?.forEach(d => {
                if (selectedDistricts.includes(d.district_en)) {
                d.mandals?.forEach(m => {
                    options.push({
                        ...m,
                        en: String(m.value) // Explicitly set 'en' for searching
                    });
                });
            }
        });
        });
        return options;
    }, [district]);

    // Add this useEffect to handle data if the modal was already open
    useEffect(() => {
    // If a draft is passed from AI, override the blank fields
        if (aiDraft) {
            console.log("Setting AI Draft:", aiDraft);
            setTitle(aiDraft.title || '');
            setContent(aiDraft.description || '');
        }
    }, [aiDraft]);

    // --- EDIT MODE: Load Data ---
    useEffect(() => {
        if (isEditing && initialPostData) {
            setTitle(initialPostData.title || '');
            setContent(initialPostData.content || '');
            setExistingImageUrls(initialPostData.imageUrls || []);
            setExistingVideoUrl(initialPostData.videoUrl || null); // Load existing video URL

            // Detect and set the correct active media type
            if (initialPostData.mediaType) {
                setMediaType(initialPostData.mediaType);
            } else if (initialPostData.videoUrl) {
                setMediaType('video');
            } else {
                setMediaType('image');
            }

            // Use our Multi-select helper to find the right options
            setPostType(findOptionsMulti(postTypeOptions, initialPostData.postType));
            setSubCategory(findOptionsMulti(subCategoryOptions, initialPostData.subCategory));
            
            // For Wishes type, load the explicitly saved states
            if (initialPostData.postType?.includes(SPECIAL_WISHES_POST_TYPE)) {
                setState(findOptionsMulti(stateOptions, initialPostData.state));
            }
        }
    }, [isEditing, initialPostData, postTypeOptions, subCategoryOptions, stateOptions]);

    // Load District/Constituency/Mandal once options become available
    useEffect(() => {
        if (isEditing && initialPostData && districtOptions.length > 0) {
            setDistrict(findOptionsMulti(districtOptions, initialPostData.district));
        }
    }, [isEditing, initialPostData, districtOptions]);

    useEffect(() => {
        if (isEditing && initialPostData && constituencyOptions.length > 0) {
            setConstituency(findOptionsMulti(constituencyOptions, initialPostData.constituency));
        }
    }, [isEditing, initialPostData, constituencyOptions]);

    useEffect(() => {
        if (isEditing && initialPostData && mandalOptions.length > 0) {
            setMandal(findOptionsMulti(mandalOptions, initialPostData.mandal));
        }
    }, [isEditing, initialPostData, mandalOptions]);


    const handleImageChange = (e) => {
        if (e.target.files.length > 0) {
            const files = Array.from(e.target.files).slice(0, 3 - existingImageUrls.length);
            setImages(files);
        }
    };

    // --- VIDEO VALIDATION HANDLER ---
    const handleVideoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Check file type
        if (!file.type.startsWith('video/')) {
            setError("Please select a valid video file.");
            return;
        }

        // Validate Duration
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = function () {
            window.URL.revokeObjectURL(video.src);
            const duration = video.duration;
            if (duration > 121) { // 1 second buffer
                setError("Video duration cannot exceed 1 minute.");
                setVideoFile(null);
                setVideoPreview(null);
            } else {
                setVideoDuration(duration);
                setVideoFile(file);
                setVideoPreview(URL.createObjectURL(file));
                setError("");
            }
        };
        video.src = URL.createObjectURL(file);
    };

    const handleRemoveExistingImage = (imageUrlToRemove) => {
        setExistingImageUrls(prev => prev.filter(url => url !== imageUrlToRemove));
        setImagesToDelete(prev => [...prev, imageUrlToRemove]);
    };

    const handleContentBlur = () => {
    setContent(prev => cleanTextSpacing(prev));
};

const handleTitleBlur = () => {
    setTitle(prev => prev.replace(/\s+/g, ' ').trim());
};

// Update the wordCount memo to count correctly based on cleanTextSpacing
const wordCount = useMemo(() => {
    if (!content) return 0;
    const clean = cleanTextSpacing(content);
    return clean.trim().split(/\s+/).filter(Boolean).length;
}, [content]);

    // Add these new states for profile completion
    const [profileData, setProfileData] = useState({
        phoneNumber: currentUser?.profile?.phoneNumber || '',
        gender: currentUser?.profile?.gender || '',
        age: currentUser?.profile?.age || '',
        dob: currentUser?.profile?.dob || '',
        state: currentUser?.profile?.location?.state || '',
        district: currentUser?.profile?.location?.district || ''
    });

    // Check if profile is incomplete
    const isProfileIncomplete = useMemo(() => {
        if (forceHideProfile) return false;
        const p = currentUser?.profile;
        return !p?.phoneNumber || !p?.gender || !p?.age || !p?.dob || !p?.location?.state;
    }, [currentUser, forceHideProfile]);

    const handlePostNews = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

         // Apply the smart text spacing logic
    const cleanedTitle = title.replace(/\s+/g, ' ').trim();
    const cleanedContent = cleanTextSpacing(content);
    // --- ADD THIS CHECK TO PREVENT "Draft News" SUBMISSION ---
        const lowerTitle = cleanedTitle.toLowerCase();
        if (lowerTitle === 'news draft' || lowerTitle === 'draft news' || !lowerTitle) {
            setError("దయచేసి 'News Draft' శీర్షికను మార్చి, సరైన తెలుగు శీర్షికను నమోదు చేయండి. (Please replace 'News Draft' with a proper title)");
            return;
        }
    const cleanWordCount = cleanedContent.split(/\s+/).filter(Boolean).length;

    if (cleanWordCount > WORD_LIMIT) {
        setError(`Description cannot exceed ${WORD_LIMIT} words.`);
        return;
    }

        if (!firebaseUser) {
            setError('You must be logged in to post.');
            return;
        }

        if (postType.length === 0) { 
            setError("Please select at least one Post Type.");
            return;
        }

        // const hasImages = images.length > 0 || existingImageUrls.length > 0;
        // if (!hasImages) {
        //     setError('At least one image is required.');
        //     return;
        // }

        // MEDIA VALIDATION
        if (mediaType === 'image') {
            if (images.length === 0 && existingImageUrls.length === 0) {
                setError('At least one image is required.');
                return;
            }
        } else if (mediaType === 'video') {
            if (!videoFile && !existingVideoUrl) {
                setError('Please select a video file.');
                return;
            }
        }

        // NEW: Profile Validation if incomplete
    if (isProfileIncomplete) {
        if (!profileData.phoneNumber || !profileData.gender || !profileData.age || !profileData.dob || !profileData.state || !profileData.district) {
            setError("దయచేసి మీ పూర్తి వివరాలను నమోదు చేయండి (Please fill all profile details).");
            return;
        }
    }

    setPosting(true);
    try {
        // --- CHANGE THIS SECTION ---
        // Instead of calling the /admin/ API, update Firestore directly
        if (isProfileIncomplete) {
            const userRef = doc(db, 'users', firebaseUser.uid);
            await updateDoc(userRef, {
                phoneNumber: profileData.phoneNumber,
                gender: profileData.gender,
                age: parseInt(profileData.age),
                dob: profileData.dob,
                location: { 
                    state: profileData.state, 
                    district: profileData.district 
                }
            });
            setForceHideProfile(true);
            console.log("Profile updated via Frontend SDK");
        }
            let finalImageUrls = [];
            let finalVideoUrl = null;

            // UPLOAD LOGIC
            if (mediaType === 'image') {
                // Keep the filtered down existing images list
                finalImageUrls = [...existingImageUrls];
                if (images.length > 0) {
                    setUploading(true);
                    const uploadPromises = images.map(async (image) => {
                        const storageRef = ref(storage, `news_images/${firebaseUser.uid}/${Date.now()}_${image.name}`);
                        await uploadBytes(storageRef, image);
                        return getDownloadURL(storageRef);
                    });
                    const newUrls = await Promise.all(uploadPromises);
                    finalImageUrls = [...finalImageUrls, ...newUrls];
                }
                // Clear any video reference since the user chose Image mode
                finalVideoUrl = null;
            } else {
                // VIDEO UPLOAD
                // Video mode: upload a new file, or retain the existing video URL
                if (videoFile) {
                    setUploading(true);
                    const videoRef = ref(storage, `news_videos/${firebaseUser.uid}/${Date.now()}_${videoFile.name}`);
                    await uploadBytes(videoRef, videoFile);
                    finalVideoUrl = await getDownloadURL(videoRef);
                } else {
                    finalVideoUrl = existingVideoUrl;
                }
                // Clear any image references since the user chose Video mode
                finalImageUrls = [];
            }

            const rawPostTypes = postType.map(pt => pt.value.type);

            let finalStates = [];
            if (shouldShowLocationDropdowns) {
                finalStates = state.map(s => s.value.state_en);
            } else {
                finalStates = rawPostTypes.filter(type => 
                    type === 'Andhra Pradesh' || type === 'Telangana'
                );
            }

            // ISO String timestamp for clean JSON serialization over HTTP
            const currentTimestamp = new Date().toISOString();

            const postData = {
                title: cleanedTitle,
                content: cleanedContent, 
                postType: rawPostTypes,
                state: finalStates,
                district: district.map(d => d.value.district_en),
                constituency: constituency.map(c => c.value),
                mandal: mandal.map(m => m.value),
                subCategory: subCategory.map(sc => sc.value),
                imageUrls: finalImageUrls,
                videoUrl: finalVideoUrl,
                mediaType: mediaType,
                status: 'pending',
                userName: currentUser?.profile?.name || firebaseUser.displayName || 'Anonymous', 
                authorName: currentUser?.profile?.name || firebaseUser.displayName || 'Anonymous',
                lastEditedAt: currentTimestamp,
            };

            if (isEditing && initialPostData) {
                const idToken = await firebaseUser.getIdToken(true);
                
                await axios.post(`https://api-5iacy4tfya-uc.a.run.app/user-news`, {
                    ...postData,
                    newsId: initialPostData.id,
                    userId: firebaseUser.uid,
                }, {
                    headers: { Authorization: `Bearer ${idToken}` }
                });
                
                setSuccess('News updated! AI is currently moderating the content...');
            } else {
                const idToken = await firebaseUser.getIdToken(true);
                await axios.post(`${API_BASE_URL}/user-news`, {
                    ...postData,
                    userId: firebaseUser.uid,
                    createdAt: currentTimestamp, // Swapped client-side serverTimestamp() for currentTimestamp
                }, { headers: { Authorization: `Bearer ${idToken}` } });
                setSuccess('News posted successfully!');
            }

            setTimeout(() => {
                onClose();
                window.location.reload();
            }, 2000);
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || 'Failed to submit. Try again.');
        } finally {
            setPosting(false);
            setUploading(false);
        }
    };

    // This function allows searching in both Telugu (label) and English (en)
    const customFilter = (option, searchText) => {
        if (!searchText) return true;
        const search = searchText.toLowerCase();
        const label = (option.label || "").toLowerCase();
        const english = (option.data.en || "").toLowerCase();
        return label.includes(search) || english.includes(search);
    };

    // Callback that is triggered once the user hits the Autofill Draft button
    const handleDraftApproved = (draft) => {
        setTitle(draft.title || '');
        setContent(draft.description || '');
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <div className="modal-header">
                    <h2>{isEditing ? 'Edit Post' : 'Create a Post'}</h2>
                    <button onClick={onClose} className="close-button">&times;</button>
                </div>
                <div className="modal-body">
                    {/* --- INLINE CHAT ASSISTANT INSERTED HERE AT THE TOP OF THE BODY --- */}
                    {!isEditing && (
                        <Chatbot 
                            isInline={true} 
                            onDraftApproved={handleDraftApproved} 
                        />
                    )}

                    <form onSubmit={handlePostNews}>
                        {isProfileIncomplete && (
                            <div className="profile-completion-warning">
                                <h4>⚠️ ముఖ్య గమనిక (Important Note):</h4>
                                <p>మీ భద్రత కోసం మరియు తెలుగులో వార్తలు రాస్తూ డబ్బు సంపాదించడానికి, దయచేసి క్రింది వివరాలను ఖచ్చితంగా నమోదు చేయండి.</p>
                                <p className="english-sub">(Enter the required details for your safety purpose and earn money by posting news in Telugu)</p>

                <div className="form-row">
                    <div className="form-group">
                        <label>Phone Number</label>
                        <input 
                            type="tel" 
                            placeholder="10 digit number"
                            value={profileData.phoneNumber}
                            onChange={e => setProfileData({...profileData, phoneNumber: e.target.value})}
                        />
                    </div>
                    <div className="form-group">
                        <label>Gender</label>
                        <select value={profileData.gender} onChange={e => setProfileData({...profileData, gender: e.target.value})}>
                            <option value="">Select</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                        </select>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Age</label>
                        <input type="number" value={profileData.age} onChange={e => setProfileData({...profileData, age: e.target.value})} />
                    </div>
                    <div className="form-group">
                        <label>Date of Birth</label>
                        <input type="date" value={profileData.dob} onChange={e => setProfileData({...profileData, dob: e.target.value})} />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>State</label>
                        <select value={profileData.state} onChange={e => setProfileData({...profileData, state: e.target.value, district: ''})}>
                            <option value="">Select State</option>
                            {locationData.map(s => <option key={s.state_en} value={s.state_en}>{s.state_te}</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>District</label>
                        <select value={profileData.district} onChange={e => setProfileData({...profileData, district: e.target.value})}>
                            <option value="">Select District</option>
                            {locationData.find(s => s.state_en === profileData.state)?.districts.map(d => (
                                <option key={d.district_en} value={d.district_en}>{d.district_te}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <hr />
            </div>
        )}
                        <div className="form-group">
                            <label>Title</label>
                            <input value={title} onChange={(e) => setTitle(e.target.value)} required />
                        </div>
                        <div className="form-group">
    <label>Content</label>
    <textarea 
        value={content} 
        onChange={(e) => setContent(e.target.value)} 
        onBlur={handleContentBlur} // Automatically cleans multi-spaces on blur
        rows="4" 
        required
    ></textarea>
    <div className={`word-counter ${wordCount > WORD_LIMIT ? 'error' : ''}`}>{wordCount}/{WORD_LIMIT}</div>
</div>
                        <div className="form-group">
                            <label>Post Type</label>
                            <Select
                                options={postTypeOptions}
                                isMulti 
                                value={postType}
                                onChange={selected => {
                                    setPostType(selected || []);
                                    setDistrict([]); setConstituency([]); setMandal([]);
                                    if (!selected?.some(opt => opt.value.type === SPECIAL_WISHES_POST_TYPE)) setState([]);
                                }}
                                placeholder="Select Types..."
                                isClearable
                            />
                        </div>

                        {/* INSERT THE NEW SUB-CATEGORY SECTION HERE */}
                        <div className="form-group">
                            <label>Category</label>
                            <Select
                                options={subCategoryOptions}
                                isMulti
                                value={subCategory}
                                onChange={setSubCategory}
                                placeholder="Select Categories..."
                                isClearable
                            />
                        </div>

                        {/* Location Logic */}
                        {(isStatePostTypeSelected || shouldShowLocationDropdowns) && (
                            <div className="form-row">
                                {shouldShowLocationDropdowns && (
                                    <div className="form-group">
                                        <label>State</label>
                                        <Select 
                                            options={stateOptions} isMulti value={state}
                                            filterOption={customFilter}
                                            onChange={s => { setState(s || []); setDistrict([]); }}
                                            placeholder="Select State..."
                                        />
                                    </div>
                                )}
                                {districtOptions.length > 0 && (
                                    <div className="form-group">
                                        <label>District</label>
                                        <Select 
                                            options={districtOptions} isMulti value={district}
                                            filterOption={customFilter}
                                            onChange={d => { setDistrict(d || []); setConstituency([]); }}
                                            placeholder="Select District..."
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {district.length > 0 && (
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Constituency</label>
                                    <Select 
                                        options={constituencyOptions} 
                                        isMulti 
                                        value={constituency} 
                                        onChange={setConstituency}
                                        filterOption={customFilter} // ADD THIS
                                        placeholder="Search constituency..." 
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Mandal</label>
                                    <Select 
                                        options={mandalOptions} 
                                        isMulti 
                                        value={mandal} 
                                        onChange={setMandal}
                                        filterOption={customFilter} // ADD THIS
                                        placeholder="Search mandal..." 
                                    />
                                </div>
                            </div>
                        )}

                        {/* --- NEW MEDIA SELECTION UI --- */}
                        <div className="media-selector-group">
                            <label>Select Media Type:</label>
                            <div className="media-type-buttons">
                                <button 
                                    type="button" 
                                    className={mediaType === 'image' ? 'active' : ''} 
                                    onClick={() => { setMediaType('image'); setError(''); }} // Clear error when switching
                                >
                                    Images (Max 3)
                                </button>
                                <button 
                                    type="button" 
                                    className={mediaType === 'video' ? 'active' : ''} 
                                    onClick={() => { setMediaType('video'); setError(''); }} // Clear error when switching
                                >
                                Video (Max 2 min)
                                </button>
                            </div>
                        </div>

                        {/* --- 1. IMAGE SECTION --- */}
{mediaType === 'image' && (
    <div className="form-group">
        <label>Images (Max 3)</label>
        <div className="existing-images-preview">
            {existingImageUrls.map((url, idx) => (
                <div key={idx} className="existing-image-item">
                    <img src={url} alt="post" />
                    {/* Added className="remove-image-btn" */}
                    <button 
                        type="button" 
                        className="remove-image-btn" 
                        onClick={() => handleRemoveExistingImage(url)}
                    >
                        &times;
                    </button>
                </div>
            ))}
        </div>
        {(images.length + existingImageUrls.length) < 3 && (
            <input type="file" accept="image/*" multiple onChange={handleImageChange} />
        )}
    </div>
)}
                        {/* --- 2. VIDEO SECTION --- */}
                        {mediaType === 'video' && (
                            <div className="form-group">
                                <label>Video (Up to 2 minutes)</label>
                                {(videoPreview || existingVideoUrl) ? (
                                    <div className="video-preview-container">
                                        <video 
                                            src={videoPreview || existingVideoUrl} 
                                            controls 
                                            className="preview-video-element" 
                                            style={{ width: '100%', maxHeight: '200px' }}
                                        />
                                        <button 
                                            type="button" 
                                            className="remove-video" 
                                            onClick={() => {
                                                setVideoFile(null); 
                                                setVideoPreview(null); 
                                                setVideoDuration(0);
                                                setExistingVideoUrl(null); // Clear database reference on user request
                                            }}
                                        >
                                        Remove Video
                                        </button>
                                    </div>
                                ) : (
                                    <input type="file" accept="video/*" onChange={handleVideoChange} />
                                )}
                                {videoDuration > 0 && (
                                    <p className="duration-info">Duration: {Math.round(videoDuration)} seconds</p>
                                )}
                            </div>
                        )}

                        {error && <p className="error-message">{error}</p>}
                        {success && <p className="success-message">{success}</p>}

                        <div className="modal-footer">
                            <button type="button" className="button-secondary" onClick={onClose}>Cancel</button>
                            <button type="submit" className="button-primary" disabled={posting || uploading}>
                                {posting ? 'Processing...' : (isEditing ? 'Update Post' : 'Submit Post')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PostNewsModal;